/**
 * The mock API behind MSW — a stateful stand-in for the FastAPI service.
 *
 * This is what makes the W1-F acceptance bar possible: **every screen works with zero
 * backend running**. It answers the 19 product paths the console's screens call: the
 * committed spec now has 25, and Wave 2's session/auth/oauth surface is typed in
 * `src/api/client.ts` but deliberately *not* mocked (there is no sign-in screen in this
 * wave, and a mock that minted session tokens would misrepresent the product). Payloads
 * are shaped by the frozen schemas, and the data comes from W0's golden fixtures
 * (`tests/fixtures/plumber-local/**`, copied byte-for-byte into `src/mocks/data/`).
 *
 * Deliberate rules:
 *  - The numbers are derived from the fixtures, not invented: ops estimates come from
 *    the same arithmetic as `estimate_operations`, push ids come from the canned mutate
 *    response, dry-run outcomes come from `validate_only*.json`.
 *  - The mock enforces the product's guardrails, so the UI cannot demo a path the real
 *    system would refuse: no push without a passing preflight *and* a passing dry run,
 *    `confirm_paused` mandatory, acknowledged ops >= the estimate, campaigns PAUSED.
 *  - State lives in `localStorage` (page context — MSW handlers resolve in the page, not
 *    in the service worker), so a reload keeps the demo state and Playwright gets a
 *    clean slate per browser context.
 */
import type {
  AdAccount,
  Blueprint,
  BlueprintDiff,
  BlueprintSummary,
  ErrorResponse,
  GenerateRequest,
  IngestRequest,
  JobAccepted,
  JobStatus,
  KeywordIdea,
  KeywordIdeaPage,
  MetricPage,
  MetricRow,
  OpsBudgetStatus,
  OpsEstimate,
  Platform,
  PreflightReport,
  ProjectCreateRequest,
  ProjectSummary,
  PushRequest,
  PushResult,
  PushState,
  PushStatusResponse,
  ReversalPlan,
  ValidationReport,
} from '@/api/types'
import { estimateOperations } from '@/lib/ops'
import { blueprintDiff } from './diff'

import fixtureBlueprint from './data/plumber-local.blueprint.json'
import fixtureExpectedOps from './data/plumber-local.expected_ops_estimate.json'
import googleConversionActions from './data/plumber-local.google.conversion_actions.json'
import googleCustomer from './data/plumber-local.google.customer.json'
import googleNoTracking from './data/plumber-local.google.customer_no_conversion_tracking.json'
import googleMutate from './data/plumber-local.google.mutate_response.json'
import googleMetrics from './data/plumber-local.google.metrics_stream.json'
import googleValidateOnly from './data/plumber-local.google.validate_only.json'
import googleValidateOnlyRejected from './data/plumber-local.google.validate_only_rejected.json'
import microsoftAccounts from './data/plumber-local.microsoft.accounts.json'
import microsoftPush from './data/plumber-local.microsoft.push_response.json'
import microsoftReport from './data/plumber-local.microsoft.report.json'

// ---------------------------------------------------------------------------
// Deterministic ids and clocks
// ---------------------------------------------------------------------------

/** Deterministic UUID (v4 shape) from a seed, so ids are stable across reloads/tests. */
export function mockUuid(seed: string): string {
  let h = 0x811c9dc5
  const bytes: number[] = []
  for (let i = 0; i < 16; i += 1) {
    for (const ch of `${seed}#${i}`) {
      h ^= ch.charCodeAt(0)
      h = Math.imul(h, 0x01000193) >>> 0
    }
    h ^= h >>> 15
    h = Math.imul(h, 0x2545f491) >>> 0
    bytes.push((h >>> 8) & 0xff)
  }
  bytes[6] = (bytes[6]! & 0x0f) | 0x40
  bytes[8] = (bytes[8]! & 0x3f) | 0x80
  const hex = bytes.map((b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export type MockScenario = 'default' | 'dry-run-failure' | 'quota-exhausted'

const STORAGE_KEY = 'adforge.console.mock.v2'
const SCENARIO_KEY = 'adforge.console.scenario'

export const utcDate = (d: Date = new Date()): string => d.toISOString().slice(0, 10)

// ---------------------------------------------------------------------------
// Fixture-derived projections (documented in src/mocks/README.md)
// ---------------------------------------------------------------------------

type AnyRecord = Record<string, unknown>

interface MutateProjection {
  changed: Record<string, string[]>
  reversalSteps: Array<{ action: 'remove'; entity: string; external_ref: string }>
  campaignCount: number
  adGroupCount: number
}

const MUTATE_ENTITY_BY_KEY: Record<string, string> = {
  campaignBudgetResult: 'campaign_budget',
  campaignResult: 'campaign',
  adGroupResult: 'ad_group',
  adGroupCriterionResult: 'keyword',
  campaignCriterionResult: 'criteria',
  adGroupAdResult: 'responsive_search_ad',
  assetResult: 'asset',
  assetLinkResult: 'asset_link',
}

/** Read ids out of the canned Google mutate response instead of inventing them. */
function projectGoogleMutate(): MutateProjection {
  const responses = ((googleMutate as AnyRecord).mutateOperationResponses ?? []) as AnyRecord[]
  const changed: Record<string, string[]> = {}
  const campaignIds: string[] = []
  const adGroupIds: string[] = []
  const otherIds: string[] = []
  for (const entry of responses) {
    for (const [key, value] of Object.entries(entry)) {
      const entity = MUTATE_ENTITY_BY_KEY[key]
      if (!entity) continue
      const record = value as AnyRecord
      const id = String(record.id ?? record.resourceName ?? '')
      const shortId = id.includes('/') ? id.split('/').pop()! : id
      changed[entity] = [...(changed[entity] ?? []), shortId]
      if (entity === 'campaign') campaignIds.push(shortId)
      else if (entity === 'ad_group') adGroupIds.push(shortId)
      else otherIds.push(shortId)
    }
  }
  // Reversal order is children before parents: the only order both platforms accept.
  const externalRefs = (
    [
      ['responsive_search_ad', changed.responsive_search_ad ?? []],
      ['keyword', changed.keyword ?? []],
      ['ad_group', adGroupIds],
      ['criteria', changed.criteria ?? []],
      ['campaign', campaignIds],
      ['campaign_budget', changed.campaign_budget ?? []],
      ['asset', changed.asset ?? []],
    ] as Array<[string, string[]]>
  )
  const reversalSteps = externalRefs.flatMap(([entity, ids]) =>
    ids.map((external_ref) => ({ action: 'remove' as const, entity, external_ref })),
  )
  void otherIds
  return {
    changed,
    reversalSteps,
    campaignCount: campaignIds.length,
    adGroupCount: adGroupIds.length,
  }
}

const GOOGLE_MUTATE = projectGoogleMutate()

interface GoogleMetricsRow {
  campaign: { id: string; name: string }
  ad_group: { id: string; name: string }
  metrics: {
    impressions: number
    clicks: number
    costMicros: string | number
    conversions: number
    conversionsValue: number
    invalidClickRate: number
  }
  segments: { date: string }
}

function googleMetricRows(accountId: string): MetricRow[] {
  const batches = ((googleMetrics as AnyRecord).batches ?? []) as Array<{
    results: GoogleMetricsRow[]
  }>
  const rows: MetricRow[] = []
  for (const batch of batches) {
    for (const result of batch.results ?? []) {
      const impressions = result.metrics.impressions
      const clicks = result.metrics.clicks
      const cost = Number(result.metrics.costMicros)
      rows.push({
        platform: 'google',
        utc_date: result.segments.date,
        account_external_id: accountId,
        level: 'ad_group',
        campaign_external_id: result.campaign.id,
        campaign_name: result.campaign.name,
        ad_group_external_id: result.ad_group.id,
        ad_group_name: result.ad_group.name,
        keyword_text: null,
        match_type: null,
        impressions,
        clicks,
        cost_micros: cost,
        conversions: result.metrics.conversions,
        conversion_value_micros: Math.round(result.metrics.conversionsValue * 1_000_000),
        ctr: impressions === 0 ? 0 : clicks / impressions,
        average_cpc_micros: clicks === 0 ? 0 : Math.round(cost / clicks),
        invalid_click_rate: result.metrics.invalidClickRate ?? null,
        currency_code: 'GBP',
        blueprint_version: 1,
      })
    }
  }
  return rows
}

function microsoftMetricRows(accountId: string): MetricRow[] {
  const rawRows = ((microsoftReport as AnyRecord).Rows ?? []) as Array<Record<string, unknown>>
  const seen = new Set<string>()
  const rows: MetricRow[] = []
  for (const raw of rawRows) {
    // The canned file repeats its rows (three report pages); collapse by key.
    const key = `${raw.TimePeriod}:${raw.AdGroupId}`
    if (seen.has(key)) continue
    seen.add(key)
    const impressions = Number(raw.Impressions ?? 0)
    const clicks = Number(raw.Clicks ?? 0)
    const costMicros = Math.round(Number(raw.Spend ?? 0) * 1_000_000)
    rows.push({
      platform: 'microsoft',
      utc_date: String(raw.TimePeriod),
      account_external_id: accountId,
      level: 'ad_group',
      campaign_external_id: String(raw.CampaignId ?? ''),
      campaign_name: String(raw.CampaignName ?? ''),
      ad_group_external_id: String(raw.AdGroupId ?? ''),
      ad_group_name: String(raw.AdGroupName ?? ''),
      keyword_text: null,
      match_type: null,
      impressions,
      clicks,
      cost_micros: costMicros,
      conversions: Number(raw.Conversions ?? 0),
      conversion_value_micros: Math.round(Number(raw.Revenue ?? 0) * 1_000_000),
      ctr: impressions === 0 ? 0 : clicks / impressions,
      average_cpc_micros: clicks === 0 ? 0 : Math.round(costMicros / clicks),
      invalid_click_rate: null,
      currency_code: String(raw.CurrencyCode ?? 'GBP'),
      blueprint_version: 1,
    })
  }
  return rows
}

/** Accounts, straight from the canned customer/SearchAccounts fixtures. */
export function mockAccounts(): AdAccount[] {
  const customer = (googleCustomer as AnyRecord).customer as AnyRecord
  const noTracking = googleNoTracking as AnyRecord
  const msAccount = (microsoftAccounts as AnyRecord).Accounts as AnyRecord[]
  const ms = msAccount[0] ?? {}
  return [
    {
      platform: 'google',
      external_id: String(customer.id),
      credential_id: mockUuid('credential:google:primary'),
      name: `${String(customer.descriptiveName).split(' — ')[0]}`,
      currency_code: String(customer.currencyCode),
      time_zone: String(customer.timeZone),
      status: 'enabled',
      test_account: Boolean(customer.testAccount),
      is_manager: false,
      manager_external_id: String((googleCustomer as AnyRecord).login_customer_id ?? '') || null,
      billing_configured: true,
      conversion_tracking_ok: true,
      conversion_action_count: ((googleConversionActions as AnyRecord).results as unknown[]).length,
    },
    {
      // The fixture's declared failure path: preflight must refuse this account, so the
      // console has a real "blocked" account to demonstrate rather than a fake one.
      platform: 'google',
      external_id: String(noTracking.login_customer_id),
      credential_id: mockUuid('credential:google:secondary'),
      name: 'Rapid Plumbing Manchester — no conversion tracking',
      currency_code: String(customer.currencyCode),
      time_zone: String(customer.timeZone),
      status: 'enabled',
      test_account: false,
      is_manager: true,
      manager_external_id: null,
      billing_configured: Boolean((noTracking.billing_setup as AnyRecord)?.status === 'APPROVED'),
      conversion_tracking_ok: false,
      conversion_action_count: 0,
    },
    {
      platform: 'microsoft',
      external_id: String(ms.Id ?? '1234567890'),
      credential_id: mockUuid('credential:microsoft:primary'),
      name: String(ms.Name ?? 'Microsoft Ads account'),
      currency_code: String(ms.CurrencyCode ?? 'GBP'),
      time_zone: String(ms.TimeZone ?? 'Europe/London'),
      status: 'enabled',
      test_account: true,
      is_manager: false,
      manager_external_id: String(ms.CustomerId ?? '') || null,
      billing_configured: true,
      conversion_tracking_ok: true,
      conversion_action_count: 2,
    },
  ]
}

function microsoftPushResult(): PushResult {
  const record = microsoftPush as AnyRecord
  const campaign = (record.Campaign ?? record.campaign ?? {}) as AnyRecord
  const adGroups = ((record.AdGroups ?? record.ad_groups ?? []) as AnyRecord[]) ?? []
  const changed: Record<string, string[]> = {}
  if (campaign.Id) changed.campaign = [String(campaign.Id)]
  if (adGroups.length > 0) changed.ad_group = adGroups.map((g) => String(g.Id))
  return {
    ok: Boolean(record.ok ?? true),
    platform: 'microsoft',
    state: 'live_paused',
    dry_run: false,
    ops_spent: 0,
    requests_made: 0,
    errors: [],
    warnings: [],
    changed,
    started_at: null,
    finished_at: null,
    idempotency_key: null,
    reversal_plan_id: null,
  }
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

interface StoredVersion {
  summary: BlueprintSummary
  document: Blueprint
  note: string | null
}

interface StoredProject {
  summary: ProjectSummary
  siteUrl: string
  ingest: IngestRequest | null
  seedHash: string
}

interface StoredJob {
  status: JobStatus
  kind: JobStatus['kind']
  projectId: string
  startedAt: number
  durationMs: number
  effect: 'none' | 'blueprint'
  effectApplied: boolean
}

interface StoredPush {
  id: string
  projectId: string
  blueprintId: string
  platform: Platform
  accountExternalId: string
  createdAt: number
  timeline: Array<{ state: PushState; at: number }>
  estimate: OpsEstimate
  preflight: PreflightReport
  dryRun: ValidationReport
  result: PushResult
  reversalPlan: ReversalPlan
  reverseStartedAt: number | null
  opsSpent: number
}

interface MockState {
  scenario: MockScenario
  projects: Record<string, StoredProject>
  versions: Record<string, StoredVersion>
  projectVersions: Record<string, string[]>
  jobs: Record<string, StoredJob>
  pushes: Record<string, StoredPush>
  dryRuns: Record<string, ValidationReport>
  budget: Record<Platform, OpsBudgetStatus>
  log: Array<{ method: string; path: string; at: number; status: number }>
  counter: number
}

function emptyBudget(): Record<Platform, OpsBudgetStatus> {
  return {
    google: {
      platform: 'google',
      utc_date: utcDate(),
      daily_ceiling: 2_880,
      consumed_operations: 1_290,
      reserved_operations: 0,
      available_operations: 1_590,
      access_level: 'explorer',
    },
    microsoft: {
      platform: 'microsoft',
      utc_date: utcDate(),
      daily_ceiling: 15_000,
      consumed_operations: 0,
      reserved_operations: 0,
      available_operations: 15_000,
      access_level: 'test',
    },
  }
}

function initialState(scenario: MockScenario): MockState {
  return {
    scenario,
    projects: {},
    versions: {},
    projectVersions: {},
    jobs: {},
    pushes: {},
    dryRuns: {},
    budget: emptyBudget(),
    log: [],
    counter: 0,
  }
}

let state: MockState = initialState('default')
let hydrated = false

function hasStorage(): boolean {
  return typeof localStorage !== 'undefined'
}

function hydrate(): void {
  if (hydrated) return
  hydrated = true
  if (!hasStorage()) return
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const storedScenario = localStorage.getItem(SCENARIO_KEY) as MockScenario | null
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<MockState>
      state = { ...initialState(storedScenario ?? 'default'), ...parsed, log: [] }
    } else {
      state = initialState(storedScenario ?? 'default')
    }
  } catch {
    state = initialState('default')
  }
  if (Object.keys(state.projects).length === 0) seedDemoProject()
}

function persist(): void {
  if (!hasStorage()) return
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...state, log: [] }))
  } catch {
    /* quota/private mode: the mock still works in memory */
  }
}

export function setScenario(scenario: MockScenario): void {
  hydrate()
  state.scenario = scenario
  if (hasStorage()) localStorage.setItem(SCENARIO_KEY, scenario)
  persist()
}

export function getScenario(): MockScenario {
  hydrate()
  return state.scenario
}

export function resetMockApi(scenario: MockScenario = state.scenario): void {
  state = initialState(scenario)
  hydrated = true
  seedDemoProject()
  persist()
}

export function requestLog(): Array<{ method: string; path: string; at: number; status: number }> {
  hydrate()
  return [...state.log]
}

export function recordRequest(method: string, path: string, status: number): void {
  hydrate()
  state.log.push({ method, path, at: Date.now(), status })
  if (state.log.length > 500) state.log.splice(0, state.log.length - 500)
  persist()
}

// ---------------------------------------------------------------------------
// Seeding: the demo project comes from the golden fixture
// ---------------------------------------------------------------------------

function fixtureDocument(): Blueprint {
  return structuredClone(fixtureBlueprint) as unknown as Blueprint
}

function fixtureV2(document: Blueprint): Blueprint {
  // A realistic n+1: a headline edit, an added negative, a budget change and a new
  // keyword. Deterministic, so the diff screen has something true to show on first run.
  const next = structuredClone(document)
  next.version = document.version + 1
  const campaign = next.campaigns[0]!
  campaign.daily_budget_micros = campaign.daily_budget_micros + 1_000_000
  const adGroup = campaign.ad_groups[0]!
  const headline = adGroup.ads[0]?.headlines[1]
  if (headline) {
    headline.text = '24/7 Emergency Plumber'
    headline.generated_by = null
    headline.evidence = 'human edit in the console'
  }
  adGroup.keywords.push({
    text: 'emergency plumber near me',
    match_type: 'PHRASE',
    source: 'user',
    avg_monthly_searches: 1200,
    competition: 'HIGH',
    top_of_page_bid_low_micros: 500000,
    top_of_page_bid_high_micros: 1400000,
  })
  ;(adGroup.negatives ??= []).push({
    text: 'jobs',
    match_type: 'PHRASE',
    source: 'user',
    avg_monthly_searches: null,
    competition: null,
    top_of_page_bid_low_micros: null,
    top_of_page_bid_high_micros: null,
  })
  next.meta.notes = 'v2: budget raised to £40/day, human-edited headline, two keyword edits.'
  return next
}

function summaryFor(
  document: Blueprint,
  ids: { id: string; projectId: string },
  createdAt: string,
  pushState: PushState = 'draft',
): BlueprintSummary {
  const adGroups = document.campaigns.flatMap((c) => c.ad_groups)
  const keywords = adGroups.reduce((sum, g) => sum + g.keywords.length, 0)
  const ads = adGroups.reduce((sum, g) => sum + g.ads.length, 0)
  return {
    id: ids.id,
    project_id: ids.projectId,
    version: document.version,
    schema_version: String((document as unknown as AnyRecord).schema_version ?? '1.0'),
    push_state: pushState,
    blueprint_hash: `sha256:${mockUuid(`hash:${ids.id}`).replace(/-/g, '')}`,
    created_at: createdAt,
    campaign_count: document.campaigns.length,
    ad_group_count: adGroups.length,
    keyword_count: keywords,
    ad_count: ads,
    policy_lint_passed: true,
  }
}

function registerVersion(
  projectId: string,
  document: Blueprint,
  createdAt: string,
  note: string | null,
): StoredVersion {
  const versionId = mockUuid(`blueprint:${projectId}:v${document.version}`)
  const stored: StoredVersion = {
    summary: summaryFor(document, { id: versionId, projectId }, createdAt),
    document,
    note,
  }
  state.versions[versionId] = stored
  const list = state.projectVersions[projectId] ?? []
  if (!list.includes(versionId)) list.push(versionId)
  state.projectVersions[projectId] = list
  return stored
}

function seedDemoProject(): void {
  const projectId = mockUuid('project:demo:plumber-local')
  const document = fixtureDocument()
  const source = (fixtureBlueprint as unknown as { created_from: Record<string, unknown> })
    .created_from
  const project: StoredProject = {
    summary: {
      id: projectId,
      name: 'Rapid Plumbing Manchester',
      slug: 'rapid-plumbing-manchester',
      site_url: String(source.site_url),
      status: 'ready',
      created_at: '2026-09-17T09:00:00Z',
      blueprint_count: 0,
      latest_blueprint_version: 2,
      latest_push_state: null,
    },
    siteUrl: String(source.site_url),
    ingest: null,
    seedHash: `sha256:${mockUuid('seed:demo').replace(/-/g, '')}`,
  }
  state.projects[projectId] = project
  const v1 = registerVersion(projectId, document, '2026-09-17T09:00:00Z', 'Generated from site crawl')
  const v2Document = fixtureV2(document)
  registerVersion(projectId, v2Document, '2026-09-17T09:40:00Z', 'Human edits after review')
  void v1
  project.summary.blueprint_count = 2
  state.counter += 1
}

export function demoProjectId(): string {
  hydrate()
  const [id] = Object.keys(state.projects)
  return id ?? mockUuid('project:demo:plumber-local')
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function notFound(detail: string, code = 'NOT_FOUND'): ErrorResponse {
  return { code, detail, field_path: null }
}

function nextId(seed: string): string {
  state.counter += 1
  return mockUuid(`${seed}:${state.counter}`)
}

function projectOrThrow(projectId: string): StoredProject {
  hydrate()
  const project = state.projects[projectId]
  if (!project) throw new MockHttpError(404, notFound(`No project ${projectId}`))
  return project
}

export class MockHttpError extends Error {
  readonly status: number
  readonly body: ErrorResponse

  constructor(status: number, body: ErrorResponse) {
    super(body.detail)
    this.name = 'MockHttpError'
    this.status = status
    this.body = body
  }
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export function listAdAccounts(platform?: string): { accounts: AdAccount[] } {
  hydrate()
  const accounts = mockAccounts()
  if (platform === 'google' || platform === 'microsoft') {
    return { accounts: accounts.filter((a) => a.platform === platform) }
  }
  return { accounts }
}

export function getOpsBudget(): OpsBudgetStatus[] {
  hydrate()
  const budget = { ...state.budget }
  budget.google = { ...budget.google, utc_date: utcDate() }
  budget.microsoft = { ...budget.microsoft, utc_date: utcDate() }
  budget.google.available_operations = Math.max(
    0,
    budget.google.daily_ceiling -
      budget.google.consumed_operations -
      budget.google.reserved_operations,
  )
  budget.microsoft.available_operations = Math.max(
    0,
    budget.microsoft.daily_ceiling -
      budget.microsoft.consumed_operations -
      budget.microsoft.reserved_operations,
  )
  return [budget.google, budget.microsoft]
}

export function createProject(body: ProjectCreateRequest): ProjectSummary {
  hydrate()
  const id = nextId(`project:${body.name}`)
  const now = new Date().toISOString()
  const project: StoredProject = {
    summary: {
      id,
      name: body.name,
      slug: body.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, ''),
      site_url: body.site_url,
      status: 'ingesting',
      created_at: now,
      blueprint_count: 0,
      latest_blueprint_version: null,
      latest_push_state: null,
    },
    siteUrl: body.site_url,
    ingest: null,
    seedHash: `sha256:${mockUuid(`seed:${body.site_url}`).replace(/-/g, '')}`,
  }
  state.projects[id] = project
  persist()
  return project.summary
}

export function getProject(projectId: string): ProjectSummary {
  const project = projectOrThrow(projectId)
  const versions = state.projectVersions[projectId] ?? []
  const latest = versions.at(-1)
  return {
    ...project.summary,
    blueprint_count: versions.length,
    latest_blueprint_version: latest ? state.versions[latest]!.document.version : null,
    latest_push_state: latest ? state.versions[latest]!.summary.push_state : null,
  }
}

function jobDuration(kind: JobStatus['kind']): number {
  switch (kind) {
    case 'ingest':
      return 5_000
    case 'research':
      return 6_000
    case 'generate':
      return 7_000
    case 'metrics_sync':
      return 4_000
    default:
      return 5_000
  }
}

function createJob(
  projectId: string,
  kind: JobStatus['kind'],
  effect: StoredJob['effect'] = 'none',
): JobAccepted {
  const jobId = nextId(`job:${kind}`)
  const now = Date.now()
  state.jobs[jobId] = {
    status: {
      job_id: jobId,
      kind,
      status: 'queued',
      progress: 0,
      message: null,
      result_ref: null,
      error: null,
      updated_at: new Date(now).toISOString(),
    },
    kind,
    projectId,
    startedAt: now,
    durationMs: jobDuration(kind),
    effect,
    effectApplied: false,
  }
  persist()
  return {
    job_id: jobId,
    kind,
    status: 'queued',
    enqueued_at: new Date(now).toISOString(),
    eta_seconds: Math.round(jobDuration(kind) / 1000),
    stream_url: `/api/v1/jobs/${jobId}/events`,
  }
}

export function ingestSite(projectId: string, body: IngestRequest): JobAccepted {
  const project = projectOrThrow(projectId)
  project.ingest = body
  project.siteUrl = body.site_url ?? project.siteUrl
  project.summary.status = 'ingesting'
  persist()
  return createJob(projectId, 'ingest')
}

export function startResearch(projectId: string, _body: unknown): JobAccepted {
  projectOrThrow(projectId)
  return createJob(projectId, 'research')
}

export function generateBlueprint(projectId: string, body: GenerateRequest): JobAccepted {
  const project = projectOrThrow(projectId)
  project.summary.status = 'generating'
  void body
  return createJob(projectId, 'generate', 'blueprint')
}

export function listBlueprints(projectId: string): BlueprintSummary[] {
  projectOrThrow(projectId)
  const ids = state.projectVersions[projectId] ?? []
  return ids
    .map((id) => state.versions[id]!.summary)
    .sort((a, b) => a.version - b.version)
    .map((summary) => ({ ...summary }))
}

function applyJobEffect(job: StoredJob): void {
  if (job.effectApplied) return
  job.effectApplied = true
  if (job.effect === 'blueprint') {
    const project = state.projects[job.projectId]!
    const document = fixtureDocument()
    const versionCount = (state.projectVersions[job.projectId] ?? []).length
    document.version = versionCount + 1
    document.project_id = job.projectId
    if (project.ingest?.site_url) {
      document.created_from.site_url = project.ingest.site_url
    }
    registerVersion(
      job.projectId,
      document,
      new Date().toISOString(),
      `Generated for ${project.summary.name}`,
    )
    project.summary.status = 'ready'
    project.summary.latest_blueprint_version = document.version
  }
  persist()
}

export function getJob(jobId: string): JobStatus {
  hydrate()
  const job = state.jobs[jobId]
  if (!job) throw new MockHttpError(404, notFound(`No job ${jobId}`))
  const elapsed = Date.now() - job.startedAt
  const progress = Math.min(1, elapsed / job.durationMs)
  let status: JobStatus['status'] = 'running'
  if (progress >= 1) {
    applyJobEffect(job)
    status = 'succeeded'
  }
  let message: string | null = null
  if (job.kind === 'ingest') message = 'Crawling the site and extracting offerings'
  if (job.kind === 'research') message = 'Pulling keyword ideas (1 request/second per customer id)'
  if (job.kind === 'generate') message = 'Clustering keywords and writing ad copy'
  if (progress >= 1) message = 'Done'
  const result: JobStatus = {
    ...job.status,
    status,
    progress,
    message,
    result_ref: status === 'succeeded' ? `blueprint:${job.projectId}` : null,
    updated_at: new Date(job.startedAt + Math.min(elapsed, job.durationMs)).toISOString(),
  }
  job.status = result
  return result
}

export function getBlueprint(blueprintId: string): Blueprint {
  const version = versionOrThrow(blueprintId)
  return structuredClone(version.document)
}

function versionOrThrow(blueprintId: string): StoredVersion {
  hydrate()
  const version = state.versions[blueprintId]
  if (!version) throw new MockHttpError(404, notFound(`No blueprint ${blueprintId}`))
  return version
}

export function editBlueprint(
  blueprintId: string,
  body: { document: Blueprint; note?: string | null },
): BlueprintSummary {
  const previous = versionOrThrow(blueprintId)
  const projectId = previous.summary.project_id
  const document = structuredClone(body.document)
  document.version = previous.document.version + 1
  document.project_id = projectId
  const stored = registerVersion(
    projectId,
    document,
    new Date().toISOString(),
    body.note ?? `Edited from version ${previous.document.version}`,
  )
  const project = state.projects[projectId]
  if (project) {
    project.summary.blueprint_count = (state.projectVersions[projectId] ?? []).length
    project.summary.latest_blueprint_version = document.version
  }
  persist()
  return stored.summary
}

export function diffBlueprint(blueprintId: string, against?: string): BlueprintDiff {
  const target = versionOrThrow(blueprintId)
  const projectId = target.summary.project_id
  let base: StoredVersion | undefined
  if (against) {
    base = state.versions[against]
  } else {
    const ids = state.projectVersions[projectId] ?? []
    const index = ids.indexOf(blueprintId)
    base = index > 0 ? state.versions[ids[index - 1]!] : target
  }
  if (!base) base = target
  return blueprintDiff(
    { version: base.document.version, document: base.document },
    { version: target.document.version, document: target.document },
  )
}

export function opsEstimate(blueprintId: string, platform: Platform): OpsEstimate {
  const version = versionOrThrow(blueprintId)
  return estimateOperations(version.document, platform)
}

export function preflight(blueprintId: string, adAccountId: string): PreflightReport {
  const version = versionOrThrow(blueprintId)
  const account = mockAccounts().find((a) => a.external_id === adAccountId)
  if (!account) throw new MockHttpError(404, notFound(`No ad account ${adAccountId}`))
  const blockers: PreflightReport['blockers'] = []
  const warnings: PreflightReport['warnings'] = []
  if (!account.conversion_tracking_ok) {
    blockers.push({
      code: 'NO_CONVERSION_ACTION',
      message:
        'This account has no enabled conversion action. AdForge refuses to push into an account with no conversion tracking: spend without measurement is the failure this product exists to prevent.',
      field_path: null,
      help_url: 'https://support.google.com/google-ads/answer/1722054',
      retryable: false,
    })
  }
  if (!account.billing_configured) {
    blockers.push({
      code: 'BILLING_NOT_CONFIGURED',
      message: 'Billing is not approved on this account, so the platform would reject the campaign.',
      field_path: null,
      help_url: null,
      retryable: false,
    })
  }
  if (account.conversion_tracking_ok) {
    warnings.push({
      code: 'BID_STRATEGY_TEMPLATE',
      message: `${version.document.campaigns[0]?.bid_strategy.kind ?? 'MAXIMIZE_CONVERSIONS'} will be created with no target — AdForge never manages bids after launch.`,
      field_path: null,
      help_url: null,
    })
  }
  return {
    ok: blockers.length === 0,
    platform: account.platform,
    account_external_id: account.external_id,
    conversion_actions_configured: account.conversion_tracking_ok,
    account_status: account.status,
    billing_configured: account.billing_configured,
    conversion_action_names: account.conversion_tracking_ok
      ? ((googleConversionActions as AnyRecord).results as Array<{ name: string }>).map(
          (r) => r.name,
        )
      : [],
    blockers,
    warnings,
    checked_at: new Date().toISOString(),
  }
}

export function dryRun(blueprintId: string, adAccountId: string): ValidationReport {
  const version = versionOrThrow(blueprintId)
  const account = mockAccounts().find((a) => a.external_id === adAccountId)
  if (!account) throw new MockHttpError(404, notFound(`No ad account ${adAccountId}`))
  const estimate = estimateOperations(version.document, account.platform)
  const report = buildDryRunReport(version, account.external_id, account.platform, estimate)
  state.dryRuns[`${blueprintId}:${adAccountId}`] = report
  persist()
  return report
}

function buildDryRunReport(
  version: StoredVersion,
  accountExternalId: string,
  platform: Platform,
  estimate: OpsEstimate,
): ValidationReport {
  const checked = estimate.total_operations
  if (state.scenario === 'dry-run-failure') {
    // From the fixture's canned REJECTED validate_only response: one headline fails
    // policy, addressed in the document's own path dialect so the review screen can
    // point at the exact asset.
    const rejection = googleValidateOnlyRejected as AnyRecord
    const detail = (
      (
        ((rejection.partialFailureError as AnyRecord)?.details as AnyRecord[])?.[0]
          ?.errors as Array<AnyRecord>
      )?.[0] ?? {}
    ) as AnyRecord
    const trigger = ((detail.trigger as AnyRecord)?.stringValue as string) ?? 'the flagged headline'
    void googleValidateOnly
    return {
      ok: false,
      platform,
      dry_run: true,
      checked_entities: checked,
      estimated_operations: checked,
      errors: [
        {
          code: 'POLICY_VIOLATION',
          message: `Headline claims a guaranteed outcome and was rejected by the platform: ${trigger}`,
          field_path: 'campaigns[0].ad_groups[0].ads[0].headlines[2].text',
          help_url: 'https://support.google.com/adspolicy/answer/6020955',
          retryable: false,
        },
        {
          code: 'POLICY_VIOLATION',
          message:
            'A second headline uses excessive capitalisation; rewrite it before pushing.',
          field_path: 'campaigns[0].ad_groups[1].ads[0].headlines[4].text',
          help_url: null,
          retryable: false,
        },
      ],
      warnings: [],
      raw_summary: String((rejection.partialFailureError as AnyRecord)?.message ?? ''),
      generated_at: new Date().toISOString(),
    }
  }
  const accepted = googleValidateOnly as AnyRecord
  const warnings = (((accepted.warnings as AnyRecord[]) ?? []) as AnyRecord[]).map((w) => ({
    code: String(w.code ?? 'RESOURCE_LIMIT'),
    message: String(w.message ?? ''),
    field_path: null,
    help_url: null,
  }))
  return {
    ok: true,
    platform,
    dry_run: true,
    checked_entities: checked,
    estimated_operations: Number(accepted.operations ?? checked),
    errors: [],
    warnings,
    raw_summary: `validate_only accepted ${checked} operations against ${accountExternalId} for v${version.summary.version}`,
    generated_at: new Date().toISOString(),
  }
}

const PUSH_OFFSETS: Array<{ state: PushState; at: number }> = [
  { state: 'preflight', at: 0 },
  { state: 'dry_run', at: 700 },
  { state: 'publishing_pages', at: 1_500 },
  { state: 'pushing', at: 2_300 },
  { state: 'live_paused', at: 3_200 },
]

export function pushBlueprint(
  blueprintId: string,
  body: PushRequest,
): { result: PushResult; pushId: string } {
  const version = versionOrThrow(blueprintId)
  const account = mockAccounts().find((a) => a.external_id === body.ad_account_id)
  if (!account) throw new MockHttpError(404, notFound(`No ad account ${body.ad_account_id}`))

  // Guardrails, enforced here exactly as 04-contracts.md §6 / 00-product-brief.md state
  // them. A mock that let the UI demo an illegal push would be a lie about the product.
  if (body.confirm_paused !== true) {
    throw new MockHttpError(
      422,
      notFound(
        'confirm_paused must be true: AdForge only ever creates campaigns paused.',
        'CONFIRM_PAUSED_REQUIRED',
      ),
    )
  }
  const estimate = estimateOperations(version.document, account.platform)
  if (body.acknowledged_operations < estimate.total_operations) {
    throw new MockHttpError(
      422,
      notFound(
        `acknowledged_operations (${body.acknowledged_operations}) is below the estimated cost (${estimate.total_operations}).`,
        'ACKNOWLEDGED_OPERATIONS_TOO_LOW',
      ),
    )
  }
  const report = preflight(blueprintId, account.external_id)
  if (!report.ok) {
    throw new MockHttpError(
      409,
      notFound(
        'Preflight failed, so nothing was pushed: ' + (report.blockers ?? []).map((b) => b.message).join(' '),
        'PUSH_BLOCKED_BY_PREFLIGHT',
      ),
    )
  }
  const storedDryRun = state.dryRuns[`${blueprintId}:${account.external_id}`]
  if (!storedDryRun) {
    throw new MockHttpError(
      409,
      notFound(
        'A passing dry run is mandatory before any live mutate. Run the dry run first.',
        'DRY_RUN_REQUIRED',
      ),
    )
  }
  if (!storedDryRun.ok) {
    throw new MockHttpError(
      409,
      notFound(
        'The last dry run reported errors, so the push is refused until they are fixed.',
        'PUSH_BLOCKED_BY_DRY_RUN',
      ),
    )
  }

  const budget = state.budget[account.platform]
  if (state.scenario === 'quota-exhausted' || estimate.total_operations > budget.available_operations) {
    const retryAfter = new Date(Date.now() + 6 * 60 * 60 * 1000).toISOString()
    throw new MockHttpError(
      402,
      notFound(
        `Daily operation quota cannot cover this push yet: it needs ${estimate.total_operations} operations and ${budget.available_operations} remain of ${budget.daily_ceiling} (${budget.access_level} access). Retry after ${retryAfter}.`,
        'OPS_LEASE_DENIED',
      ),
    )
  }

  const pushId = body.idempotency_key ?? nextId('push')
  const createdAt = Date.now()
  const reversalPlan: ReversalPlan = {
    plan_id: mockUuid(`reversal:${pushId}`),
    tenant_id: version.document.tenant_id,
    platform: account.platform,
    ad_account_external_id: account.external_id,
    blueprint_version: version.document.version,
    created_at: new Date(createdAt).toISOString(),
    push_attempt_id: pushId,
    reversible: true,
    notes: 'Every push produces a stored reversal plan — undo is a feature, not a support ticket.',
    steps: (
      (account.platform === 'google'
        ? GOOGLE_MUTATE.reversalSteps
        : (((microsoftPush as AnyRecord).ReversalSteps ?? []) as Array<{
            action: 'remove'
            entity: string
            external_ref: string
          }>))
    ).map((step, index) => ({
      action: step.action,
      entity: step.entity,
      external_ref: step.external_ref,
      before_state: index === 0 ? { status: 'PAUSED' } : {},
      note: null,
    })),
  }
  const result: PushResult = {
    ok: true,
    platform: account.platform,
    state: 'preflight',
    dry_run: false,
    ops_spent: 0,
    requests_made: 0,
    errors: [],
    warnings: storedDryRun.warnings,
    changed: account.platform === 'google' ? GOOGLE_MUTATE.changed : microsoftPushResult().changed,
    started_at: new Date(createdAt).toISOString(),
    finished_at: null,
    idempotency_key: pushId,
    reversal_plan_id: reversalPlan.plan_id,
  }
  state.pushes[pushId] = {
    id: pushId,
    projectId: version.summary.project_id,
    blueprintId,
    platform: account.platform,
    accountExternalId: account.external_id,
    createdAt,
    timeline: PUSH_OFFSETS.map((step) => ({ state: step.state, at: createdAt + step.at })),
    estimate,
    preflight: report,
    dryRun: storedDryRun,
    result,
    reversalPlan,
    reverseStartedAt: null,
    opsSpent: estimate.total_operations,
  }
  // budget.settle(): the daily counter moves at push time, not at the next poll.
  budget.consumed_operations += estimate.total_operations
  budget.available_operations = Math.max(
    0,
    budget.daily_ceiling - budget.consumed_operations - budget.reserved_operations,
  )
  state.versions[blueprintId]!.summary.push_state = 'preflight'
  persist()
  return { result, pushId }
}

function pushStateAt(push: StoredPush, now: number): PushState {
  if (push.reverseStartedAt !== null) {
    return now - push.reverseStartedAt >= 1_200 ? 'reversed' : 'reversing'
  }
  let current: PushState = 'draft'
  for (const step of push.timeline) {
    if (step.at <= now) current = step.state
  }
  return current
}

export function getPush(pushId: string): PushStatusResponse {
  hydrate()
  const push = state.pushes[pushId]
  if (!push) throw new MockHttpError(404, notFound(`No push ${pushId}`))
  const now = Date.now()
  const current = pushStateAt(push, now)
  const settled = current === 'live_paused' || current === 'reversed'
  const result: PushResult = {
    ...push.result,
    state: current,
    ops_spent: settled ? push.opsSpent : current === 'draft' ? 0 : Math.round(push.opsSpent / 2),
    requests_made: settled ? push.estimate.requests_required : 0,
    finished_at: settled ? new Date(push.createdAt + 3_200).toISOString() : null,
  }
  state.versions[push.blueprintId]!.summary.push_state = current
  persist()
  return {
    push_id: pushId,
    state: current,
    platform: push.platform,
    ops_estimated: push.estimate.total_operations,
    ops_spent: result.ops_spent,
    updated_at: new Date(now).toISOString(),
    preflight: push.preflight,
    dry_run: push.dryRun,
    Estimate: push.estimate,
    result,
    reversal_plan: push.reversalPlan,
  }
}

export function reversePush(pushId: string, body: { confirm: boolean; reason?: string | null }) {
  hydrate()
  const push = state.pushes[pushId]
  if (!push) throw new MockHttpError(404, notFound(`No push ${pushId}`))
  if (body.confirm !== true) {
    throw new MockHttpError(
      409,
      notFound('Reversing a live push requires an explicit confirmation.', 'CONFIRM_REQUIRED'),
    )
  }
  const current = pushStateAt(push, Date.now())
  if (current !== 'live_paused' && current !== 'failed') {
    throw new MockHttpError(
      409,
      notFound(
        `Only a live or failed push can be reversed; this push is ${current}.`,
        'INVALID_TRANSITION',
      ),
    )
  }
  push.reverseStartedAt = Date.now()
  persist()
  return {
    ...push.result,
    state: 'reversing' as PushState,
    changed: push.result.changed,
    ops_spent: push.opsSpent,
    requests_made: push.estimate.requests_required,
  } satisfies PushResult
}

export function listKeywordIdeas(projectId: string): KeywordIdeaPage {
  projectOrThrow(projectId)
  const ids = state.projectVersions[projectId] ?? []
  const latest = ids.at(-1)
  const document = latest ? state.versions[latest]!.document : null
  const seen = new Map<string, KeywordIdea>()
  for (const campaign of document?.campaigns ?? []) {
    for (const adGroup of campaign.ad_groups) {
      for (const keyword of [...adGroup.keywords, ...(adGroup.negatives ?? [])]) {
        if (seen.has(keyword.text)) continue
        // KeywordIdea follows Google's GenerateKeywordIdeas naming (low_/high_
        // top_of_page_bid_micros), which is why it is not KeywordSpec with a different
        // name — the mapping lives in the adapter, not in the console.
        seen.set(keyword.text, {
          text: keyword.text,
          avg_monthly_searches: keyword.avg_monthly_searches ?? null,
          competition: keyword.competition ?? null,
          competition_index: keyword.competition === 'HIGH' ? 80 : keyword.competition === 'MEDIUM' ? 50 : 20,
          low_top_of_page_bid_micros: keyword.top_of_page_bid_low_micros ?? null,
          high_top_of_page_bid_micros: keyword.top_of_page_bid_high_micros ?? null,
          close_variants: [],
          monthly_search_volumes: [
            { year: 2026, month: 9, searches: keyword.avg_monthly_searches ?? 0 },
          ],
        })
      }
    }
  }
  return {
    seed_hash: `sha256:${mockUuid(`seed:${projectId}`).replace(/-/g, '')}`,
    generated_at: '2026-09-17T09:05:00Z',
    cached: true,
    ideas: [...seen.values()],
  }
}

export function getMetrics(
  projectId: string,
  query: { start_date: string; end_date: string; level?: string },
): MetricPage {
  projectOrThrow(projectId)
  const ids = state.projectVersions[projectId] ?? []
  const latest = ids.at(-1)
  const pushed = Object.values(state.pushes).some(
    (push) => push.projectId === projectId && pushStateAt(push, Date.now()) === 'live_paused',
  )
  const inWindow = (row: MetricRow) =>
    row.utc_date >= query.start_date && row.utc_date <= query.end_date
  const level = query.level ?? 'ad_group'
  const rows: MetricRow[] = pushed
    ? [
        ...googleMetricRows(mockAccounts()[0]!.external_id),
        ...microsoftMetricRows(mockAccounts()[2]!.external_id),
      ].filter((row) => inWindow(row) && row.level === level)
    : []
  if (latest) {
    for (const row of rows) {
      row.blueprint_version = state.versions[latest]!.document.version
    }
  }
  return {
    window: { start_date: query.start_date, end_date: query.end_date },
    rows,
    synced_at: pushed ? new Date().toISOString() : null,
    next_cursor: null,
  }
}
const _fixtureExpectedOps = fixtureExpectedOps
export { _fixtureExpectedOps as fixtureExpectedOps }
